import toast from 'react-hot-toast';
import { uploadFilesAndComplete } from '../../utils/fileUpload';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { MODULE_UPLOAD } from '../../utils/moduleUpload.ts';
import {
  documentFileId,
  documentFileName,
  documentRowKey,
  documentSizeBytes
} from '../../utils/documentUtils';

function createPendingRowId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random()}`;
}

export type ProcessDocumentRow = {
  _clientRowId?: string;
  file?: File;
  file_id?: string | null;
  file_name?: string;
  size_bytes?: number;
  tags?: string[];
  isPendingUpload?: boolean;
};

export function createPendingProcessDocument(file: File): ProcessDocumentRow {
  return {
    _clientRowId: `pending-${createPendingRowId()}`,
    file,
    file_id: null,
    file_name: file.name,
    size_bytes: file.size,
    tags: [],
    isPendingUpload: true
  };
}

export function isPendingProcessDocument(doc: ProcessDocumentRow): boolean {
  return Boolean(doc?.file) && !documentFileId(doc);
}

/** Upload staged files (init-upload flow) and return an updated document list. */
export async function uploadPendingProcessDocuments(
  documents: ProcessDocumentRow[],
  userHash: string
): Promise<ProcessDocumentRow[]> {
  const list = Array.isArray(documents) ? documents : [];
  const pending = list.filter(isPendingProcessDocument);
  if (pending.length === 0) {
    return list;
  }

  const uploadResults = await Promise.allSettled(
    pending.map((doc) => uploadFilesAndComplete([doc.file as File], userHash, MODULE_UPLOAD.SOX_PROCESS))
  );

  const uploadedByRowKey: Record<string, { file_id?: string; filename?: string; size_bytes?: number }> = {};
  let failCount = 0;

  uploadResults.forEach((result, index) => {
    const doc = pending[index];
    const rowKey = documentRowKey(doc);
    if (result.status === 'fulfilled' && result.value?.[0]?.file_id) {
      uploadedByRowKey[rowKey] = result.value[0];
      return;
    }
    failCount += 1;
    const uploadError = result.status === 'rejected' ? result.reason : undefined;
    console.error('Process document upload failed:', uploadError ?? result);
    toast.error(resolve403ErrorMessage(uploadError, `Failed to upload ${documentFileName(doc)}`));
  });

  const next: ProcessDocumentRow[] = [];
  for (const doc of list) {
    const rowKey = documentRowKey(doc);
    const uploaded = uploadedByRowKey[rowKey];
    if (!uploaded?.file_id) {
      if (documentFileId(doc) || !doc.file) {
        next.push(doc);
      }
      continue;
    }
    next.push({
      _clientRowId: doc._clientRowId,
      file_id: uploaded.file_id,
      file_name: uploaded.filename ?? doc.file_name,
      size_bytes: uploaded.size_bytes ?? documentSizeBytes(doc),
      tags: doc.tags || []
    });
  }

  const successCount = Object.keys(uploadedByRowKey).length;
  if (successCount > 0) {
    const label = successCount === 1 ? 'file' : 'files';
    toast.success(`${successCount} ${label} uploaded and linked.`);
  }
  if (failCount > 0 && successCount === 0) {
    throw new Error('No files uploaded successfully');
  }

  return next;
}
